import { Divider, Grid } from '@mui/material';
import { memo, useCallback } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { FormikErrors, FormikTouched } from 'formik';
import { Cohabitant } from 'mutation/view-models/declaration-cohabitation';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import FormGenerality from 'shell/components/shared/form-generality/form';
import { SubjectData } from 'shell/models/person';
import { noop } from 'shell/utils/noop';
import FormSearchPerson from 'shell/components/shared/search-person';
import { transformCohabitationDeFacto } from 'shell/utils/consult-person/response-transformer/cohabitation-de-facto/cohabitation-de-facto';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { PersonalDataSearchVM } from 'shell/view-models/personal-data';

interface AccordionCohabitantFormProps {
  disabled?: boolean;
  values?: Cohabitant;
  onChange: (key: keyof Cohabitant, value: Cohabitant[keyof Cohabitant]) => void;
  onChangeValues: (values?: Cohabitant, convivente?: unknown) => void;
  touched?: FormikTouched<Cohabitant>;
  errors?: FormikErrors<Cohabitant>;
  idFamilyIstat?: string;
  idFamilyMunicipality?: string;
  setFieldValue: (key: string, value?: SubjectData) => void;
}

const AccordionCohabitantFormComponent = ({
  disabled,
  values,
  onChange,
  touched,
  errors,
  onChangeValues,
}: AccordionCohabitantFormProps): React.ReactElement => {
  const onFieldPersonChange = useCallback(
    (key, value) => {
      onChange('person', {
        ...(values?.person || {}),
        [key]: value,
      });
    },
    [values?.person, onChange]
  );

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    },
    [values?.generality, onChange]
  );

  const onChangeFieldsGenerality = useCallback(
    (vals) => {
      onChange('generality', {
        ...(values?.generality || {}),
        ...vals,
      });
    },
    [values?.generality, onChange]
  );

  const onFieldSearchChange = useCallback(
    (key, value) => {
      onChange('search', {
        ...(values?.search || {}),
        [key]: value,
      });
    },
    [values?.search, onChange]
  );

  const onFieldsSearchChange = useCallback(
    (newValues: PersonalDataSearchVM) => {
      const cohabitant = transformCohabitationDeFacto(newValues.subjectData || {});
      const generality = transformGenerality(newValues.subjectData || {});
      const personVM = transformPerson(newValues.subjectData || {});
      onChangeValues(
        {
          ...newValues,
          search: null,
          orderCoexistence: cohabitant?.orderCoexistence || '1',
          generality: generality || undefined,
          person: personVM || undefined,
        },
        newValues.subjectData
      );
    },
    [values?.search, onChangeValues]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormSearchPerson
          onChangeFields={onFieldsSearchChange}
          values={values?.search || values ? { ...values, ...values.search } : undefined}
          onChange={onFieldSearchChange}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Ordine convivenza"
          aria-label="Ordine convivenza"
          id="order-coexistence"
          fullWidth
          onChange={(event) => {
            onChange('orderCoexistence', event?.target.value);
          }}
          value={values?.orderCoexistence || ''}
          disabled={disabled}
          error={touched?.orderCoexistence && Boolean(errors?.orderCoexistence)}
          helperText={errors?.orderCoexistence}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Dati del convivente</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <FormGenerality
          disabled={disabled}
          onChangePerson={onFieldPersonChange}
          onChangeGenerality={onFieldGeneralityChange}
          onChangeFieldsGenerality={onChangeFieldsGenerality}
          person={values?.person}
          onChangeFieldsPerson={noop}
          generality={values?.generality}
          // isEditMode={isEditMode}
          errorsGenerality={errors?.generality as FormikErrors<AccordionGeneralityForm>}
          errorsPerson={errors?.person as FormikErrors<AccordionPersonForm>}
          touchedPerson={touched?.person as unknown as FormikTouched<AccordionPersonForm>}
          touchedGenerality={
            touched?.generality as unknown as FormikTouched<AccordionGeneralityForm>
          }
        />
      </Grid>
    </Grid>
  );
};

AccordionCohabitantFormComponent.displayName = 'Accordion cohabitant form component';

const AccordionCohabitantForm = memo(AccordionCohabitantFormComponent);

export default AccordionCohabitantForm;
